var _dec, _class, _PaginationPageInput;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { withStyle } from '@instructure/emotion';
import { NumberInput } from '@instructure/ui-number-input';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
parent: Pagination
id: Pagination.PageInput
---
**/
let PaginationPageInput = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_PaginationPageInput = class PaginationPageInput extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this.handleRef = el => {
      this.ref = el;
    };
    this.handleInputRef = el => {
      if (typeof this.props.inputRef === 'function') {
        this.props.inputRef(el);
      }
    };
    this.handleChange = (_event, value) => {
      let newValue = value;

      // if the last character was not a number, don't accept it
      if (value.length > 0 && isNaN(value.slice(-1))) {
        newValue = value.slice(0, -1);
      }
      this.setState({
        number: newValue ? Number(newValue) : this.MIN,
        value: newValue
      });
    };
    this.handleDecrement = event => {
      this.setNewPage(event, Math.floor(this.currentPage - 1));
    };
    this.handleIncrement = event => {
      this.setNewPage(event, Math.floor(this.currentPage + 1));
    };
    this.handleBlur = event => {
      if (Math.round(this.state.number) !== this.currentPage) {
        this.setNewPage(event, Math.round(this.state.number));
      }
    };
    this.state = this.initialState;
  }
  get currentPage() {
    return this.props.currentPageIndex + 1;
  }
  get initialState() {
    return {
      number: this.currentPage,
      value: `${this.currentPage}`
    };
  }
  get MIN() {
    return 1;
  }
  get MAX() {
    return this.props.numberOfPages;
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);

    // when the current page changes from outside (e.g.: from arrow buttons),
    // we update the initial state
    if (this.props.currentPageIndex !== prevProps.currentPageIndex) {
      this.resetInitialState();
    }
  }
  resetInitialState() {
    this.setState(this.initialState);
  }
  handleKeyDown(event) {
    const key = event.key;
    if (key.toLowerCase() === 'enter') {
      const _this$state = this.state,
        value = _this$state.value,
        number = _this$state.number;

      // If it is invalid, we reset the input to the current page
      if (value === '' || isNaN(value)) {
        this.resetInitialState();
        return;
      }
      this.setNewPage(event, number);
    }
  }
  getNumberWithinRange(n) {
    if (n < this.MIN) return this.MIN;
    if (n > this.MAX) return this.MAX;
    return n;
  }
  setNewPage(event, n) {
    const number = this.getNumberWithinRange(n);
    this.setState({
      number,
      value: `${number}`
    });
    this.props.onChange(event, number - 1);
  }
  renderLabel() {
    const _this$props3 = this.props,
      label = _this$props3.label,
      numberOfPages = _this$props3.numberOfPages,
      styles = _this$props3.styles;
    return typeof label === 'function' && label(numberOfPages) ? _jsx("span", {
      css: styles === null || styles === void 0 ? void 0 : styles.inputLabel,
      children: label(numberOfPages)
    }) : null;
  }
  renderScreenReaderLabel() {
    const _this$props4 = this.props,
      screenReaderLabel = _this$props4.screenReaderLabel,
      numberOfPages = _this$props4.numberOfPages;
    return _jsx(ScreenReaderContent, {
      children: screenReaderLabel(this.currentPage, numberOfPages)
    });
  }
  render() {
    const _this$props5 = this.props,
      styles = _this$props5.styles,
      disabled = _this$props5.disabled;
    return _jsxs("span", {
      css: styles === null || styles === void 0 ? void 0 : styles.paginationPageInput,
      ref: this.handleRef,
      "data-cid": "PaginationPageInput",
      children: [_jsx("span", {
        css: styles === null || styles === void 0 ? void 0 : styles.numberInput,
        children: _jsx(NumberInput, {
          renderLabel: this.renderScreenReaderLabel(),
          onBlur: this.handleBlur,
          onChange: this.handleChange,
          onDecrement: this.handleDecrement,
          onIncrement: this.handleIncrement,
          onKeyDown: this.handleKeyDown.bind(this),
          interaction: disabled ? 'disabled' : 'enabled',
          showArrows: false,
          value: this.state.value,
          width: "100%",
          textAlign: "center",
          inputRef: this.handleInputRef
        })
      }), this.renderLabel()]
    });
  }
}, _PaginationPageInput.displayName = "PaginationPageInput", _PaginationPageInput.componentId = 'Pagination.PageInput', _PaginationPageInput.allowedProps = allowedProps, _PaginationPageInput.defaultProps = {
  disabled: false
}, _PaginationPageInput)) || _class);
export default PaginationPageInput;
export { PaginationPageInput };